import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import { useAppData } from '../context/AppDataContext.js';
import AIService from '../services/AIService.js';

const SmartSearchResults = ({ searchTerm, aiAnalysis, onClose, onServiceSelect, loading, isOverlay = false, overlayStyle = {}, resultsRef = null }) => {
  const router = useRouter();
  const { services, searchServices, selectedCountry } = useAppData();
  const [showClassicResults, setShowClassicResults] = useState(false);
  const [traditionalResults, setTraditionalResults] = useState([]);
  const [traditionalLoading, setTraditionalLoading] = useState(false);
  const [traditionalSearchExecuted, setTraditionalSearchExecuted] = useState(false);
  const [lastTraditionalSearchTerm, setLastTraditionalSearchTerm] = useState('');

  // Helper function to get service icon by service ID
  const getServiceIcon = (serviceId) => {
    const service = services.find(s => s.service_id === serviceId);
    return service?.icon || '✨'; // Fallback to sparkle if no icon found
  };

  // Helper function to get specialty Arabic name by service ID and specialty ID
  const getSpecialtyDisplayName = (serviceId, specialtyId) => {
    if (!specialtyId) return null;
    const service = services.find(s => s.service_id === serviceId);
    if (!service?.specialties || !service?.specialties_ar) return null;
    
    const idx = service.specialties.indexOf(specialtyId);
    if (idx !== -1 && service.specialties_ar[idx]) {
      return service.specialties_ar[idx];
    }
    return null;
  };

  // Helper function to store search query in sessionStorage before navigation
  const storeQueryForQuote = () => {
    // Use pendingSearchQuery if available, otherwise use current searchTerm
    const queryToStore = searchTerm || (typeof window !== 'undefined' ? sessionStorage.getItem('pendingSearchQuery') : null);
    if (queryToStore && typeof window !== 'undefined') {
      sessionStorage.setItem('quoteDescription', queryToStore);
      // Clear pending query since we've moved it to quoteDescription
      sessionStorage.removeItem('pendingSearchQuery');
    }
  };

  // Build quote URL including AI-suggested specialty when valid for the target service
  const buildQuoteUrl = (serviceId) => {
    const suggested = aiAnalysis?.suggestedSpecialties?.[0];
    let specialtySlug = null;
    if (suggested) {
      const svc = services.find(s => s.service_id === serviceId);
      const slugs = svc?.specialties || [];
      const labelsAr = svc?.specialties_ar || [];
      if (slugs.includes(suggested)) {
        specialtySlug = suggested;
      } else {
        const idx = labelsAr.indexOf(suggested);
        if (idx >= 0 && slugs[idx]) specialtySlug = slugs[idx];
      }
    }

    // Build URL with specialty as path parameter if available
    // Note: description is now passed via sessionStorage instead of query param
    let url = `/quote/${serviceId}`;
    if (specialtySlug) {
      url += `/${specialtySlug}`;
    }
    
    return url;
  };

  // Execute traditional search automatically when component loads
  useEffect(() => {
    // Reset traditional search state if the search term changed significantly
    if (lastTraditionalSearchTerm && searchTerm !== lastTraditionalSearchTerm) {
      setTraditionalSearchExecuted(false);
      setTraditionalResults([]);
    }

    if (searchTerm && searchTerm.length >= 2 && (!traditionalSearchExecuted || searchTerm !== lastTraditionalSearchTerm)) {
      setTraditionalLoading(true);
      setTraditionalSearchExecuted(true);
      setLastTraditionalSearchTerm(searchTerm);
      
      searchServices(searchTerm).then((results) => {
        setTraditionalResults(Array.isArray(results) ? results : []);
        setTraditionalLoading(false);
      }).catch(() => {
        setTraditionalResults([]);
        setTraditionalLoading(false);
      });
    }
  }, [searchTerm, lastTraditionalSearchTerm, traditionalSearchExecuted, searchServices]);

  const LoadingContent = (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 p-3 sm:p-4 z-50 transition-colors duration-300">
        <div className="flex items-center gap-2 sm:gap-3 mb-3">
          <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-salehli-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm sm:text-base text-gray-600 dark:text-gray-300">✨ جاري تحليل طلبك بالذكاء الاصطناعي...</span>
        </div>
      </div>
  );

  // Show loading state when loading
  if (loading) {
    const content = isOverlay
      ? createPortal(<div style={overlayStyle}>{LoadingContent}</div>, document.body)
      : (
        <div className="absolute top-full left-0 right-0 mt-2">{LoadingContent}</div>
      );
    return content;
  }

  // Don't show anything if no analysis and not loading
  if (!aiAnalysis) {
    return null;
  }

  const handleServiceClick = (serviceId) => {
    // Track AI result and create quote directly
    AIService.trackAnalysisResult(aiAnalysis, 'create_quote', serviceId);
    
    // Store search query in sessionStorage for form pre-population
    storeQueryForQuote();
    
    // Route to quote creation with pre-filled data
    router.push(buildQuoteUrl(serviceId));
    onClose();
  };

  // Handle classic search result click
  const handleClassicServiceClick = (serviceId) => {
    router.push(`/service/${serviceId}`);
    onClose();
  };

  const handleCreateQuote = () => {
    // Track AI result and create quote
    AIService.trackAnalysisResult(aiAnalysis, 'create_quote', aiAnalysis.primaryService);
    
    // Store search query in sessionStorage for form pre-population
    storeQueryForQuote();
    
    // Route to quote creation with pre-filled data
    router.push(buildQuoteUrl(aiAnalysis.primaryService));
    onClose();
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-50';
    if (confidence >= 0.6) return 'text-salehli-yellow-600 bg-salehli-yellow-50';
    return 'text-orange-600 bg-orange-50';
  };

  const getConfidenceText = (confidence) => {
    if (confidence >= 0.8) return 'عالية';
    if (confidence >= 0.6) return 'متوسطة';
    return 'منخفضة';
  };

  // Determine if AI confidence is low
  const isLowConfidence = aiAnalysis && (aiAnalysis.confidence ?? 0) < 0.6;

  const handleLowConfidenceResolve = async () => {
    // Store search query before navigation
    storeQueryForQuote();
    
    // 1) Try traditional search (S3DataService via context)
    try {
      const traditional = await searchServices(searchTerm);
      if (Array.isArray(traditional) && traditional.length > 0) {
        const top = traditional[0];
        const targetServiceId = top.service_id || top.id || top.serviceId;
        if (targetServiceId) {
          AIService.trackAnalysisResult(aiAnalysis, 'fallback_traditional', targetServiceId);
          router.push(buildQuoteUrl(targetServiceId));
          onClose();
          return;
        }
      }
    } catch (e) {
      // continue to next step
    }

    // 2) Try AI fallback analysis explicitly
    try {
      const fallback = AIService.createFallbackAnalysis(searchTerm);
      const validated = AIService.validateAndEnhanceAnalysis(fallback, searchTerm);
      if (validated?.primaryService) {
        AIService.trackAnalysisResult(validated, 'fallback_ai', validated.primaryService);
        router.push(buildQuoteUrl(validated.primaryService));
        onClose();
        return;
      }
    } catch (e) {
      // continue to final fallback
    }

    // 3) Final fallback: unspecified service
    const unspecifiedId = 'unspecified';
    AIService.trackAnalysisResult(
      aiAnalysis || { confidence: 0, originalInput: searchTerm },
      'fallback_unspecified',
      unspecifiedId
    );
    router.push(buildQuoteUrl(unspecifiedId));
    onClose();
  };

  const ResultsBox = (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 z-50 transition-colors duration-300" style={{ maxHeight: 'none', overflowY: 'visible' }}>
      {/* Header with toggle between AI and Traditional */}
      <div className="p-2 sm:p-3 border-b border-gray-100 dark:border-gray-800">
          {/* AI Service Status Notice */}
        {!showClassicResults && aiAnalysis && aiAnalysis.isAIGenerated === false && (
          <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-yellow-600 dark:text-yellow-500">⚠️</span>
              <div className="flex-1">
                <p className="text-xs text-yellow-800 dark:text-yellow-200 font-medium">البحث الذكي غير متاح حالياً</p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  جاري البحث عن النتائج المتاحة
                </p>
              </div>
            </div>
          </div>
        )}        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            {!showClassicResults ? (
              <>
                {!(aiAnalysis && aiAnalysis.isAIGenerated === false) && !isLowConfidence && (
                  <>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] sm:text-sm">✨</span>
                      <p className="text-[10px] sm:text-sm font-medium text-gray-600 dark:text-gray-300">
                        دقة التحليل الذكي
                      </p>
                    </div>
                    {aiAnalysis.primaryService !== 'unspecified' && (
                      <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {`${getConfidenceText(aiAnalysis.confidence)} (${Math.round(aiAnalysis.confidence * 100)}%)`}
                      </span>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] sm:text-sm">🔍</span>
                  <h3 className="text-[10px] sm:text-sm font-medium text-gray-600 dark:text-gray-300">نتائج أخرى</h3>
                </div>
                <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  {traditionalResults.length} نتيجة
                </span>
              </>
            )}
          </div>
          
          {/* Toggle button - only show if traditional results are available */}
          {traditionalResults.length > 0 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowClassicResults(!showClassicResults);
              }}
              className="text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-md bg-gray-50 hover:bg-gray-100 text-gray-700 dark:text-gray-100 transition-colors border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 whitespace-nowrap"
            >
              {showClassicResults ? 'البحث الذكي' : 'البحث التقليدي'}
            </button>
          )}
        </div>
      </div>

      {/* Content - Either AI Results or Traditional Results */}
      {!showClassicResults ? (
        /* AI Results */
        <>
          {/* Primary Suggestion */}
          <div className="p-3 sm:p-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100">
                {aiAnalysis && aiAnalysis.isAIGenerated === false ? 
                  'النتيجة المقترحة' : 
                  'الخدمة المقترحة'
                }
              </h4>

            </div>
            
            <div 
              onClick={() => handleServiceClick(aiAnalysis.primaryService)}
              className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg cursor-pointer transition-colors group ${
                aiAnalysis && aiAnalysis.isAIGenerated === false 
                  ? 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/60 dark:hover:bg-gray-700' 
                  : 'bg-salehli-blue-50 dark:bg-salehli-blue-900/30 hover:bg-salehli-blue-100 dark:hover:bg-salehli-blue-900/50'
              }`}
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center transition-colors ${
                aiAnalysis && aiAnalysis.isAIGenerated === false
                  ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-gray-200 dark:group-hover:bg-gray-600'
                  : 'bg-salehli-blue-100 dark:bg-salehli-blue-800 group-hover:bg-salehli-blue-200 dark:group-hover:bg-salehli-blue-700'
              }`}>
                <span className="text-xl sm:text-2xl">{getServiceIcon(aiAnalysis.primaryService)}</span>
              </div>
              <div className="flex-1">
                <h5 className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                  {AIService.getServiceDisplayText(aiAnalysis.primaryService)}
                </h5>
                {aiAnalysis.suggestedSpecialties && aiAnalysis.suggestedSpecialties.length > 0 && (() => {
                  const specialtyName = getSpecialtyDisplayName(aiAnalysis.primaryService, aiAnalysis.suggestedSpecialties[0]);
                  return specialtyName ? (
                    <p className="text-[10px] sm:text-xs text-salehli-blue-700 dark:text-salehli-blue-400 font-medium mt-0.5">
                      ← {specialtyName}
                    </p>
                  ) : null;
                })()}
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">
                  اضغط هنا لبدء الطلب
                </p>
              </div>
              <svg className={`w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform ${
                aiAnalysis && aiAnalysis.isAIGenerated === false
                  ? 'text-gray-600 dark:text-gray-400'
                  : 'text-salehli-blue-600 dark:text-salehli-blue-400'
              }`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {/* Alternative Services */}
          {aiAnalysis.alternativeServices.length > 0 && (
            <div className="p-3 sm:p-4">
              <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100 mb-2 sm:mb-3">
                خدمات أخرى قد تحتاجها
              </h4>
              <div className="space-y-1 sm:space-y-2">
                {aiAnalysis.alternativeServices.slice(0, 2).map((serviceId) => (
                  <div
                    key={serviceId}
                    onClick={() => handleServiceClick(serviceId)}
                    className="flex items-center gap-2 sm:gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                      <span className="text-base sm:text-lg">{getServiceIcon(serviceId)}</span>
                    </div>
                    <div className="flex-1">
                      <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                        {AIService.getServiceDisplayText(serviceId)}
                      </span>
                      <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">اضغط هنا لبدء الطلب</p>
                    </div>
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 dark:text-gray-500 mr-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Other Service Option - Only show if primary service is not already unspecified */}
          {aiAnalysis.primaryService !== 'unspecified' && (
            <div className="p-3 sm:p-4 border-t border-gray-100 dark:border-gray-800">
              {/* Additional notice when AI fails */}
              {aiAnalysis && aiAnalysis.isAIGenerated === false && (
                <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    💡 <strong>نصيحة:</strong>
                    إذا لم تجد ما تبحث عنه، جرب خيار &laquo;خدمة أخرى&raquo; لوصف طلبك بالتفصيل
                  </p>
                </div>
              )}
              
              <div 
                onClick={() => handleServiceClick('unspecified')}
                className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition-colors group"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center group-hover:bg-gray-300 dark:group-hover:bg-gray-600 transition-colors">
                  <span className="text-lg sm:text-xl">❓</span>
                </div>
                <div className="flex-1">
                  <h5 className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300">
                    خدمة أخرى
                  </h5>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">
                    لا أجد ما أبحث عنه - سأحدد يدوياً
                  </p>
                </div>
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 dark:text-gray-500 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Traditional Search Results */
        <div className="p-3 sm:p-4">
          {traditionalLoading ? (
            <div className="flex items-center justify-center py-6 sm:py-8">
              <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-gray-300 dark:border-gray-600 border-t-transparent rounded-full animate-spin ml-2 sm:ml-3"></div>
              <span className="text-sm sm:text-base text-gray-500 dark:text-gray-400">جاري البحث...</span>
            </div>
          ) : traditionalResults.length > 0 ? (
            <div className="space-y-2 sm:space-y-3">
              {traditionalResults.map((service) => (
                <div
                  key={service.service_id}
                  onClick={() => handleClassicServiceClick(service.service_id)}
                    className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg cursor-pointer transition-colors border border-gray-100 dark:border-gray-700"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <span className="text-xl sm:text-2xl">{service.icon}</span>
                  </div>
                  <div className="flex-1">
                    <h5 className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">{service.name_ar}</h5>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{service.description_ar}</p>
                  </div>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8">
              <div className="text-2xl sm:text-4xl mb-2 sm:mb-3">🔍</div>
              <h5 className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">لا توجد نتائج إضافية</h5>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">لم يتم العثور على خدمات مطابقة أخرى</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (isOverlay) {
    return createPortal(
      <div ref={resultsRef} style={overlayStyle}>{ResultsBox}</div>,
      document.body
    );
  }

  return (
    <div ref={resultsRef} className="absolute top-full left-0 right-0 mt-2">{ResultsBox}</div>
  );
};

const SmartSearchInput = ({ 
  placeholder = "صف مشكلتك بكلماتك... مثل: يوجد تسريب في الحنفية", 
  className = "",
  onAnalysisComplete = null,
  value = '',
  onChange = null,
  onFocus: externalOnFocus = null,
  onBlur: externalOnBlur = null
}) => {
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);
  const searchButtonRef = useRef(null);
  const resultsRef = useRef(null);
  const [overlayStyle, setOverlayStyle] = useState({});
  const { selectedCountry, searchServices } = useAppData();

  // Use controlled or internal state
  const searchTerm = value !== undefined ? value : internalSearchTerm;
  const setSearchTerm = onChange || setInternalSearchTerm;

  // Calculate overlay position relative to input
  const updateOverlayPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setOverlayStyle({
      position: 'fixed',
      top: rect.bottom + 8, // similar to mt-2
      left: rect.left,
      width: rect.width,
      zIndex: 9999
    });
  }, []);

  // Debounced AI analysis
  const analyzeInput = useCallback(async (input) => {
    if (!input || input.trim().length < 1) {
      setAiAnalysis(null);
      setShowResults(false);
      return;
    }

    // Store search query in sessionStorage early so it persists through login/registration flow
    // This ensures unauthenticated users don't lose their query when redirected to login
    if (input.trim().length >= 3) {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('pendingSearchQuery', input.trim());
      }
    }

    setLoading(true);
    setShowResults(true);
    
    // Update overlay position when showing results
    setTimeout(() => updateOverlayPosition(), 0);

    try {
      const analysis = await AIService.analyzeServiceRequest(input, selectedCountry, searchServices);
      let finalAnalysis = analysis;

      // The AI service now handles traditional search and rule-based fallbacks internally
      // No additional processing needed here

      setAiAnalysis(finalAnalysis);
      
      if (onAnalysisComplete) {
        onAnalysisComplete(finalAnalysis);
      }
    } catch (error) {
      setAiAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCountry, onAnalysisComplete, searchServices, updateOverlayPosition]);

  // Handle input changes with debouncing
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for AI analysis - trigger for any non-empty query
    if (value.trim().length >= 1) {
      debounceTimer.current = setTimeout(() => {
        analyzeInput(value);
      }, 1000); // 1 second delay for auto-trigger
    }
  };

  useEffect(() => {
    if (!showResults) return;
    updateOverlayPosition();
    const handler = () => updateOverlayPosition();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [showResults, updateOverlayPosition]);

  // Handle input focus
  const handleFocus = () => {
    if (searchTerm.length >= 1 && aiAnalysis) {
      setShowResults(true);
      updateOverlayPosition();
    } else if (searchTerm.trim().length >= 1) {
      // Retrigger analysis if we have text but no analysis yet
      analyzeInput(searchTerm);
    }
    // Call external onFocus if provided
    if (externalOnFocus) {
      externalOnFocus();
    }
  };

  // Handle input blur
  const handleBlur = () => {
    // Call external onBlur if provided
    if (externalOnBlur) {
      externalOnBlur();
    }
  };

  // Handle clicking outside the component to close results
  useEffect(() => {
    if (!showResults) return;

    const handleClickOutside = (event) => {
      // Don't close if clicking on input, search button, or results box
      if (
        inputRef.current?.contains(event.target) ||
        searchButtonRef.current?.contains(event.target) ||
        resultsRef.current?.contains(event.target)
      ) {
        return;
      }
      
      setShowResults(false);
    };

    // Add event listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showResults]);

  // Handle manual search trigger
  const handleManualSearch = () => {
    if (searchTerm.trim().length >= 1) {
      // Clear any existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      // Ensure overlay positioning is updated
      updateOverlayPosition();
      // Trigger AI analysis immediately for manual search
      analyzeInput(searchTerm);
    }
  };

  // Handle Enter key to retrigger analysis
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleManualSearch();
    }
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="w-full py-3 sm:py-4 px-4 sm:px-6 text-base sm:text-lg rounded-lg border-2 border-gray-200 dark:border-gray-600 focus:border-salehli-blue-500 focus:outline-none text-right bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
          dir="rtl"
          ref={inputRef}
        />
        
        {/* Search Icon - Always visible and clickable */}
        <button
          type="button"
          onClick={handleManualSearch}
          ref={searchButtonRef}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:cursor-not-allowed"
          disabled={loading || searchTerm.trim().length < 1}
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-salehli-blue-500 border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <svg 
              className={`w-5 h-5 transition-colors ${
                searchTerm.trim().length >= 1 
                  ? 'text-salehli-blue-500 hover:text-salehli-blue-600' 
                  : 'text-gray-400'
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </button>

        {/* AI Indicator - Shows next to search icon when there's AI analysis */}
        {searchTerm.length >= 5 && aiAnalysis && !loading && (
          <div className="absolute left-10 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">AI</span>
          </div>
        )}
      </div>

      {/* Smart Search Results */}
      {showResults && (
        <SmartSearchResults
          searchTerm={searchTerm}
          aiAnalysis={aiAnalysis}
          loading={loading}
          isOverlay={true}
          overlayStyle={overlayStyle}
          resultsRef={resultsRef}
          onClose={() => setShowResults(false)}
          onServiceSelect={(serviceId) => {
          }}
        />
      )}
    </div>
  );
};

export default SmartSearchInput;
